import { computed, ref } from 'vue';
import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';

/**
 * Who we are talking to the API as.
 *
 * Ported from lasti.fi's session, which was checked against accounts.puida.com's
 * live discovery document: `code` only, PKCE `S256` only, RS256. The SPA is a
 * public client with PKCE and no secret, against Sulje's OWN accounts
 * organization (D15) — a Hinguere account does not sign in here.
 *
 * Deliberately NOT configured, as in lasti:
 *  - the nonce check stays on, and a nonce is always sent (see `signIn`);
 *  - `loadUserInfo` stays off: the ID token has everything needed.
 *
 * `VITE_DEV_AUTH=1` swaps in the development headers the API accepts when
 * SULJE_DEV_AUTH=1. Never in a production build: the API refuses that flag.
 */

const DEV_AUTH = import.meta.env.VITE_DEV_AUTH === '1';
const DEV_KEY = 'sulje.dev.identity';

const issuer = import.meta.env.VITE_AUTH_ISSUER ?? '';
const clientId = import.meta.env.VITE_AUTH_CLIENT_ID ?? '';

/**
 * Must match a registered redirect URI exactly — no wildcards, and a trailing
 * slash is a different URI. lasti found that an unregistered one does not fail
 * visibly: the authorize endpoint answers 200 with an "Invalid Redirect URI"
 * page. To register: https://sulje.com/auth/callback and
 * http://localhost:9080/auth/callback.
 */
const redirectUri =
  import.meta.env.VITE_AUTH_REDIRECT_URI ?? `${globalThis.location.origin}/auth/callback`;

export const usingDevAuth = DEV_AUTH;

// --- development identity ---------------------------------------------------

export interface DevIdentity {
  subject: string;
  email: string;
}

function readDevIdentity(): DevIdentity | null {
  try {
    const raw = globalThis.localStorage?.getItem(DEV_KEY);
    return raw ? (JSON.parse(raw) as DevIdentity) : null;
  } catch {
    return null;
  }
}

const devIdentity = ref<DevIdentity | null>(DEV_AUTH ? readDevIdentity() : null);

export function setDevIdentity(next: DevIdentity | null): void {
  devIdentity.value = next;
  try {
    if (next) globalThis.localStorage?.setItem(DEV_KEY, JSON.stringify(next));
    else globalThis.localStorage?.removeItem(DEV_KEY);
  } catch {
    // Private mode: the identity lasts for this tab, which is enough to develop with.
  }
}

// --- real identity ----------------------------------------------------------

export const userManager = DEV_AUTH
  ? null
  : new UserManager({
      authority: issuer,
      client_id: clientId,
      redirect_uri: redirectUri,
      post_logout_redirect_uri: globalThis.location.origin,
      response_type: 'code',
      scope: 'openid profile email',
      // sessionStorage: a smaller blast radius than localStorage for a token,
      // and the refresh token restores the session on the next visit anyway.
      userStore: new WebStorageStateStore({ store: globalThis.sessionStorage }),
      automaticSilentRenew: true,
      revokeTokensOnSignout: true,
    });

const currentUser = ref<User | null>(null);
const ready = ref(DEV_AUTH);

export const isReady = computed(() => ready.value);
export const isSignedIn = computed(() =>
  DEV_AUTH ? Boolean(devIdentity.value) : Boolean(currentUser.value),
);

export async function initSession(): Promise<void> {
  if (!userManager) return;
  currentUser.value = await userManager.getUser();
  // An expired token is worse than none: it produces 401s that look like a bug.
  if (currentUser.value?.expired) currentUser.value = null;

  userManager.events.addUserLoaded((next) => {
    currentUser.value = next;
  });
  userManager.events.addUserUnloaded(() => {
    currentUser.value = null;
  });
  // Renewal failed: the session is over. Clearing it shows the landing page
  // instead of a screen making 401s.
  userManager.events.addSilentRenewError(() => {
    currentUser.value = null;
  });
  ready.value = true;
}

export async function signIn(next?: string): Promise<void> {
  if (DEV_AUTH) {
    globalThis.location.assign(`/dev-sign-in${next ? `?next=${encodeURIComponent(next)}` : ''}`);
    return;
  }
  // The nonce is supplied explicitly: oidc-client-ts only generates one when
  // the response type includes `id_token`, and this is a pure `code` flow.
  // `state` carries the path to return to; the redirect URI cannot.
  await userManager?.signinRedirect({
    nonce: crypto.randomUUID(),
    ...(next ? { state: next } : {}),
  });
}

export async function completeSignIn(): Promise<User | null> {
  if (!userManager) return null;
  const next = await userManager.signinRedirectCallback();
  currentUser.value = next;
  return next;
}

export async function signOut(): Promise<void> {
  if (DEV_AUTH) {
    setDevIdentity(null);
    return;
  }
  if (!userManager) return;

  // accounts.puida.com publishes NO end_session_endpoint, and
  // `signoutRedirect()` throws "No end session endpoint" without one — sign
  // out did nothing at all in production. So: revoke the tokens at the IdP
  // (it does publish a revocation endpoint, so they stop working everywhere,
  // not just here), then forget them locally.
  //
  // The accounts.puida.com browser session itself survives — "Sign in" again
  // may not ask for a password. That is the IdP's single sign-on, and ending
  // it needs an end-session endpoint there.
  try {
    await userManager.revokeTokens(['access_token', 'refresh_token']);
  } catch {
    // Revocation failing (offline, IdP down) must not stop the local sign-out.
  }
  await userManager.removeUser();
  currentUser.value = null;
}

/** The one place in the app that knows how a request is authenticated. */
export function authHeaders(): Record<string, string> {
  if (DEV_AUTH) {
    const who = devIdentity.value;
    return who ? { 'X-Sulje-Dev-Subject': who.subject, 'X-Sulje-Dev-Email': who.email } : {};
  }
  const token = currentUser.value?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Configuration problems should be visible at startup, not as a blank redirect. */
export function configurationProblem(): string | null {
  if (DEV_AUTH) return null;
  if (!issuer) return 'VITE_AUTH_ISSUER is not set.';
  if (!clientId) return 'VITE_AUTH_CLIENT_ID is not set.';
  if (!/^https:\/\/.+\/[0-9a-f-]{36}$/i.test(issuer)) {
    return `VITE_AUTH_ISSUER must be organization-scoped (…/<organization-id>). Got: ${issuer}`;
  }
  return null;
}
